import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Star, X } from "lucide-react";
import { Button, ResultCelebration, StepDots } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { FeedbackPlayQuestion, FeedbackSubmitResult } from "@ec2/contracts";
import { useTRPC } from "../lib/api";
import { generalError, type LocalizedError } from "../lib/errors";
import { usePasskeyPrompt } from "../state/hooks";
import { GroupBonusView } from "../components/GroupBonusView";
import { Sheet } from "../components/Sheet";
import { PasskeyPrompt } from "../components/PasskeyPrompt";
import { ErrorLine, LoadingLine } from "../components/common";

/** Client-side answer state per question (shape mirrors FeedbackAnswerInput). */
interface DraftAnswer {
  numericValue?: number;
  textValue?: string;
  optionIds?: string[];
}

function isAnswered(question: FeedbackPlayQuestion, draft: DraftAnswer | undefined): boolean {
  if (!draft) return false;
  switch (question.kind) {
    case "stars":
    case "scale":
      return draft.numericValue !== undefined;
    case "freetext":
      return (draft.textValue ?? "").trim().length > 0;
    case "single_choice":
    case "multi_choice":
      return (draft.optionIds ?? []).length > 0;
  }
}

const FREETEXT_MAX = 2000;

/**
 * Feedback questionnaire player: one question per screen (stepper, like the
 * quiz), Back on all but the first, Skip on optional questions, Submit at the
 * end — ONE submit call carries the whole questionnaire.
 */
export function FeedbackStepperScreen({ questId }: { questId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const view = useQuery({
    ...trpc.quest.getFeedback.queryOptions({ questId }),
    retry: false,
    staleTime: Infinity,
  });
  const submit = useMutation(trpc.quest.submitFeedback.mutationOptions());
  const [index, setIndex] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, DraftAnswer>>({});
  const [result, setResult] = useState<FeedbackSubmitResult | null>(null);
  const [showGroupBonus, setShowGroupBonus] = useState(false);
  const [error, setError] = useState<LocalizedError | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const passkey = usePasskeyPrompt();
  const [showPasskey, setShowPasskey] = useState(false);

  if (view.isLoading) return <LoadingLine />;
  // Result renders before the query-error branch: after submit, invalidation
  // refetches getFeedback into CONFLICT — the thank-you must still show.
  if (result) {
    if (showGroupBonus && result.groupBonus) {
      return (
        <main
          className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center px-6"
          data-testid="ec-fb-result"
        >
          <GroupBonusView bonus={result.groupBonus} onDone={() => void navigate({ to: "/quests" })} />
          {showPasskey && <PasskeyPrompt onDone={() => setShowPasskey(false)} />}
        </main>
      );
    }
    return (
      <main
        className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center px-6"
        data-testid="ec-fb-result"
      >
        <ResultCelebration
          status="solved"
          title={t("ec2.feedback.stepper.thanks")}
          amount={result.bucksAwarded}
          amountLabel={t("ec2.celebration.earned", { amount: result.bucksAwarded })}
          actionLabel={t("ec2.celebration.backToQuests")}
          onAction={() => {
            if (result.groupBonus) {
              setShowGroupBonus(true);
              return;
            }
            void navigate({ to: "/quests" });
          }}
        />
        {showPasskey && <PasskeyPrompt onDone={() => setShowPasskey(false)} />}
      </main>
    );
  }

  if (view.isError || !view.data) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6">
        <ErrorLine error={generalError(view.error)} className="text-base" />
        <Button variant="ghost" onClick={() => void navigate({ to: "/quests" })}>
          {t("ec2.celebration.backToQuests")}
        </Button>
      </main>
    );
  }

  const play = view.data;
  const total = play.questions.length;
  const question = play.questions[Math.min(index, total - 1)];

  const finish = async (finalDrafts: Record<string, DraftAnswer>) => {
    try {
      const submitted = await submit.mutateAsync({
        questId,
        answers: play.questions
          .filter((q) => isAnswered(q, finalDrafts[q.id]))
          .map((q) => {
            const draft = finalDrafts[q.id]!;
            return {
              questionId: q.id,
              ...(draft.numericValue !== undefined ? { numericValue: draft.numericValue } : {}),
              ...(draft.textValue?.trim() ? { textValue: draft.textValue.trim() } : {}),
              ...(draft.optionIds?.length ? { optionIds: draft.optionIds } : {}),
            };
          }),
      });
      setResult(submitted);
      await queryClient.invalidateQueries();
      if (passkey.shouldPrompt) {
        passkey.markPrompted();
        setShowPasskey(true);
      }
    } catch (err) {
      setError(generalError(err));
    }
  };

  const advance = (nextDrafts: Record<string, DraftAnswer>) => {
    setError(null);
    if (index + 1 < total) {
      setIndex(index + 1);
    } else {
      void finish(nextDrafts);
    }
  };

  const setDraft = (draft: DraftAnswer) => {
    if (!question) return;
    setDrafts({ ...drafts, [question.id]: draft });
  };

  const draft = question ? drafts[question.id] : undefined;
  const answered = question ? isAnswered(question, draft) : false;
  const isLast = index + 1 >= total;

  const toggleOption = (optionId: string) => {
    if (!question) return;
    const current = draft?.optionIds ?? [];
    const next =
      question.kind === "single_choice"
        ? [optionId]
        : current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId];
    setDraft({ optionIds: next });
  };

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col px-4 pb-8" data-testid="ec-fb">
      <div className="flex items-center justify-between pt-4">
        <StepDots
          count={total}
          active={index}
          label={t("ec2.feedback.stepper.progress", { current: index + 1, total })}
        />
        <button
          type="button"
          onClick={() => setConfirmLeave(true)}
          aria-label={t("ec2.feedback.stepper.leave")}
          data-testid="ec-fb-leave"
          className="flex size-10 items-center justify-center rounded-full text-fg-secondary focus-visible:outline-2 focus-visible:outline-primary"
        >
          <X size={22} aria-hidden />
        </button>
      </div>
      <div aria-hidden className="mt-2 h-0.5 overflow-hidden rounded-full bg-surface-overlay">
        <div
          className="h-full bg-accent transition-[width]"
          style={{ width: `${(index / total) * 100}%` }}
        />
      </div>

      {question && (
        <div data-testid={`ec-fb-q-${index}`}>
          <h1 className="mt-8 font-display text-[30px] leading-[1.1] font-bold tracking-[-0.02em] text-fg">
            {question.label}
          </h1>
          {!question.required && (
            <p className="mt-1 text-sm text-fg-muted">{t("ec2.feedback.stepper.optional")}</p>
          )}

          {question.kind === "stars" && (
            <div
              className="mt-8 flex justify-center gap-2"
              role="radiogroup"
              aria-label={question.label}
            >
              {[1, 2, 3, 4, 5].map((score) => {
                const active = (draft?.numericValue ?? 0) >= score;
                return (
                  <button
                    key={score}
                    type="button"
                    role="radio"
                    aria-checked={draft?.numericValue === score}
                    aria-label={t("ec2.feedback.stepper.starsLabel", { score })}
                    onClick={() => setDraft({ numericValue: score })}
                    data-testid={`ec-fb-stars-${score}`}
                    className="flex size-14 items-center justify-center rounded-(--radius-chip) focus-visible:outline-2 focus-visible:outline-primary"
                  >
                    <Star
                      size={34}
                      aria-hidden
                      className={active ? "fill-accent text-accent" : "text-fg-muted"}
                    />
                  </button>
                );
              })}
            </div>
          )}

          {question.kind === "scale" && (
            <div className="mt-8">
              <div className="flex flex-wrap justify-center gap-2" role="radiogroup" aria-label={question.label}>
                {Array.from(
                  { length: (question.scaleMax ?? 10) - (question.scaleMin ?? 0) + 1 },
                  (_, i) => (question.scaleMin ?? 0) + i,
                ).map((value) => {
                  const isSelected = draft?.numericValue === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setDraft({ numericValue: value })}
                      data-testid={`ec-fb-scale-${value}`}
                      className={
                        `flex size-12 items-center justify-center rounded-(--radius-chip) font-display text-lg font-[650] tabular-nums ` +
                        `focus-visible:outline-2 focus-visible:outline-primary ` +
                        (isSelected ? "bg-primary text-primary-fg" : "bg-surface-overlay text-fg-secondary")
                      }
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
              {(question.minLabel || question.maxLabel) && (
                <div className="mt-2 flex justify-between text-sm text-fg-muted">
                  <span>{question.minLabel}</span>
                  <span>{question.maxLabel}</span>
                </div>
              )}
            </div>
          )}

          {(question.kind === "single_choice" || question.kind === "multi_choice") && (
            <div
              className="mt-6 flex flex-col gap-3"
              role={question.kind === "multi_choice" ? "group" : "radiogroup"}
              aria-label={question.label}
            >
              {question.options.map((option, oi) => {
                const isSelected = (draft?.optionIds ?? []).includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    role={question.kind === "multi_choice" ? "checkbox" : "radio"}
                    aria-checked={isSelected}
                    onClick={() => toggleOption(option.id)}
                    data-testid={`ec-fb-opt-${oi}`}
                    className={
                      `flex min-h-14 w-full items-center gap-3 rounded-(--radius-chip) border px-4 py-3 text-left text-base text-fg ` +
                      `focus-visible:outline-2 focus-visible:outline-primary ` +
                      (isSelected ? "border-2 border-primary bg-primary/8" : "border-border bg-surface")
                    }
                  >
                    {question.kind === "multi_choice" && (
                      <span
                        aria-hidden
                        className={`flex size-5 items-center justify-center rounded-md border ${isSelected ? "border-primary bg-primary text-primary-fg" : "border-border"}`}
                      >
                        {isSelected ? "✓" : ""}
                      </span>
                    )}
                    <span className="flex-1">{option.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {question.kind === "freetext" && (
            <textarea
              value={draft?.textValue ?? ""}
              onChange={(e) =>
                setDraft({ textValue: e.target.value.slice(0, FREETEXT_MAX) })
              }
              rows={5}
              maxLength={FREETEXT_MAX}
              placeholder={t("ec2.feedback.stepper.textPlaceholder")}
              aria-label={question.label}
              data-testid="ec-fb-text"
              className="mt-6 w-full rounded-(--radius-card) border border-border bg-surface px-4 py-3 text-base text-fg placeholder:text-fg-muted focus-visible:outline-2 focus-visible:outline-primary"
            />
          )}

          <div className="mt-6 flex flex-col gap-2">
            <Button
              variant="primary"
              disabled={(question.required && !answered) || submit.isPending}
              onClick={() => advance(drafts)}
              data-testid={isLast ? "ec-fb-submit" : "ec-fb-next"}
            >
              {isLast ? t("ec2.feedback.stepper.submit") : t("ec2.feedback.stepper.next")}
            </Button>
            {!question.required && !answered && (
              <Button
                variant="ghost"
                disabled={submit.isPending}
                onClick={() => advance(drafts)}
                data-testid="ec-fb-skip"
              >
                {t("ec2.feedback.stepper.skip")}
              </Button>
            )}
            {index > 0 && (
              <Button
                variant="ghost"
                disabled={submit.isPending}
                onClick={() => setIndex(index - 1)}
                data-testid="ec-fb-back"
              >
                {t("ec2.feedback.stepper.back")}
              </Button>
            )}
          </div>
        </div>
      )}
      <ErrorLine error={error} className="mt-4" />

      {confirmLeave && (
        <Sheet label={t("ec2.feedback.stepper.leaveWarning")} onClose={() => setConfirmLeave(false)}>
          <div className="flex flex-col gap-4">
            <p className="pr-8 text-base text-fg">{t("ec2.feedback.stepper.leaveWarning")}</p>
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                onClick={() => void navigate({ to: "/quests" })}
                data-testid="ec-fb-leave-confirm"
              >
                {t("ec2.feedback.stepper.leave")}
              </Button>
              <Button variant="primary" onClick={() => setConfirmLeave(false)}>
                {t("ec2.feedback.stepper.stay")}
              </Button>
            </div>
          </div>
        </Sheet>
      )}
    </main>
  );
}
